import type { FreeResult } from '../apiClient';
import {
  BOTTLENECK_META,
  BOTTLENECK_ONE_MOVE,
  PERSPECTIVE_PATTERN_CONTENT,
  PERSPECTIVE_PATTERN_LABELS,
  WF_DIMENSION_LABELS,
  computeOverallScore,
  getHpGrade,
  getPerspectiveIntensityLabel,
  type PerspectivePatternContent,
} from './content';

/**
 * Pure per-type view-model derivation for the free result. Mirrors the Replit result
 * pages (results.tsx / work-fit-results.tsx / perspective-results.tsx): the API
 * supplies the canonical fields; presentation values (HP grade, intensity, blended,
 * top-N ordering) are computed here so the components stay thin.
 */

export interface BottleneckResultView {
  kind: 'bottleneck';
  primaryType: string;
  shortExplanation: string;
  hpScore: number;
  grade: string;
  gradeLabel: string;
  scores: { bottleneck: number; executionReadiness: number; recoveryGap: number; clarity: number };
  topBottlenecks: Array<{ key: string; label: string; explanation: string; action: string }>;
  oneMove: string;
}

export interface WorkFitResultView {
  kind: 'work_fit';
  primaryType: string;
  shortExplanation: string;
  topDimensions: Array<{ key: string; label: string }>;
  /**
   * The profile slug. Production's free result keys its recognition paragraph,
   * culture risks and interview question off it.
   */
  typeSlug: string | null;
}

export interface PerspectivePatternScore {
  key: string;
  label: string;
  score: number;
}

export interface PerspectiveResultView {
  kind: 'perspective';
  primaryType: string;
  shortExplanation: string;
  intensityLabel: string;
  isBlended: boolean;
  secondaryPatternLabel: string | null;
  patternContent: PerspectivePatternContent | null;
  topPatterns: PerspectivePatternScore[];
  /**
   * The patterns beneath the top three. Production renders them blurred behind
   * a lock rather than hiding them, so the customer can see how much of the map
   * they are not being shown.
   */
  lockedPatterns: PerspectivePatternScore[];
  lockedPatternCount: number;
}

export type ResultView = BottleneckResultView | WorkFitResultView | PerspectiveResultView;

function sortedScores(categoryScores: Record<string, number>): Array<{ key: string; score: number }> {
  return Object.entries(categoryScores)
    .map(([key, score]) => ({ key, score }))
    .sort((a, b) => b.score - a.score);
}

function buildBottleneck(result: FreeResult): BottleneckResultView {
  const hpScore = Math.round(
    computeOverallScore({
      bottleneckScore: result.bottleneckScore,
      executionReadinessScore: result.executionReadinessScore,
      recoveryDeficitScore: result.recoveryDeficitScore,
      clarityScore: result.clarityScore,
    }),
  );
  const { grade, label } = getHpGrade(hpScore);
  const topBottlenecks = result.topCategories
    .map((key) => {
      const meta = BOTTLENECK_META[key];
      return meta ? { key, label: meta.label, explanation: meta.explanation, action: meta.action } : null;
    })
    .filter((v): v is { key: string; label: string; explanation: string; action: string } => v !== null);

  return {
    kind: 'bottleneck',
    primaryType: result.primaryType,
    shortExplanation: result.shortExplanation,
    hpScore,
    grade,
    gradeLabel: label,
    scores: {
      bottleneck: Math.round(result.bottleneckScore ?? 0),
      executionReadiness: Math.round(result.executionReadinessScore ?? 0),
      recoveryGap: Math.round(result.recoveryDeficitScore ?? 0),
      clarity: Math.round(result.clarityScore ?? 0),
    },
    topBottlenecks,
    oneMove: BOTTLENECK_ONE_MOVE[result.primaryType] ?? BOTTLENECK_ONE_MOVE['The Scattered Builder'] ?? '',
  };
}

function buildWorkFit(result: FreeResult): WorkFitResultView {
  return {
    kind: 'work_fit',
    primaryType: result.primaryType,
    shortExplanation: result.shortExplanation,
    typeSlug: result.typeSlug,
    topDimensions: result.topCategories.slice(0, 2).map((key) => ({
      key,
      label: WF_DIMENSION_LABELS[key] ?? key,
    })),
  };
}

function buildPerspective(result: FreeResult): PerspectiveResultView {
  const ranked = sortedScores(result.categoryScores);
  const dominantScore = ranked[0]?.score ?? 0;
  const secondaryKey = result.secondaryPatternKey ?? null;

  return {
    kind: 'perspective',
    primaryType: result.primaryType,
    shortExplanation: result.shortExplanation,
    intensityLabel: getPerspectiveIntensityLabel(dominantScore),
    isBlended: result.isBlended ?? false,
    secondaryPatternLabel: secondaryKey ? (PERSPECTIVE_PATTERN_LABELS[secondaryKey] ?? secondaryKey) : null,
    patternContent: PERSPECTIVE_PATTERN_CONTENT[result.typeSlug] ?? null,
    topPatterns: ranked.slice(0, 3).map(({ key, score }) => ({
      key,
      label: PERSPECTIVE_PATTERN_LABELS[key] ?? key,
      score,
    })),
    // Remaining patterns are locked (blurred) for free users (Replit parity).
    lockedPatterns: ranked.slice(3).map(({ key, score }) => ({
      key,
      label: PERSPECTIVE_PATTERN_LABELS[key] ?? key,
      score,
    })),
    lockedPatternCount: Math.max(0, ranked.length - 3),
  };
}

export function buildResultView(result: FreeResult): ResultView {
  switch (result.assessmentType) {
    case 'bottleneck':
      return buildBottleneck(result);
    case 'work_fit':
      return buildWorkFit(result);
    case 'perspective':
      return buildPerspective(result);
  }
}
